import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, NavigationCancel, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { EnvironmentService } from './services/environment.service';
import { FacilityConfig, Style } from 'src/environments/environment.interface';
import { Title } from '@angular/platform-browser';
import { AuthService } from './services/auth.service';
import { environment } from 'src/environments/environment';
declare let $: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [
        Location, {
            provide: LocationStrategy,
            useClass: PathLocationStrategy
        }
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    settings: FacilityConfig;
    location: any;
    routerSubscription: any;
    logoutReason: string | null = null;
    private readonly destroy$ = new Subject<void>();

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private titleService:Title,
        private _environmentService: EnvironmentService,
        private _authService: AuthService
    ) {}

    ngOnInit(){
        this._authService.onLogoutReason().pipe(takeUntil(this.destroy$)).subscribe(reason => {
            this.logoutReason = reason;
        });
        this.recallJsFuntions();
        this.settings = this._environmentService.getFacilityConfig();
        this.applyBranding(this.settings.style);
        this.setMetaTags(this.settings.meta);
        
        // Check for access token in URL query parameters to bypass login (local environment only)
        if (environment.envName === 'local') {
            this.checkAccessTokenInUrl();
        }
        
        // Initialize location for existing logged-in users
        this._authService.initializeLocationIfNeeded().catch(err => {
            console.error('Failed to initialize location:', err);
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Checks for access token in URL query parameters and authenticates the user if found.
     * Supports 'token' and 'access_token' query parameters.
     * Removes the token from URL after processing for security.
     */
    private async checkAccessTokenInUrl(): Promise<void> {
        // Parse query parameters from the current URL
        const urlParams = new URLSearchParams(window.location.search);
        let accessToken = urlParams.get('token') || urlParams.get('access_token');
        
        if (accessToken) {
            try {
                const tokenSet = await this._authService.setAccessToken(accessToken);
                if (tokenSet) {
                    // Remove token from URL for security
                    urlParams.delete('token');
                    urlParams.delete('access_token');
                    const newQueryString = urlParams.toString();
                    const newUrl = window.location.pathname + (newQueryString ? '?' + newQueryString : '') + window.location.hash;
                    this.router.navigateByUrl(newUrl, { replaceUrl: true });
                } else if (!this.logoutReason) {
                    console.error('Failed to authenticate with provided access token');
                }
            } catch (error) {
                console.error('Error authenticating with access token:', error);
            }
        }
    }

    private applyBranding(style: Style) {
        if(style.common.colors.primary) {
            document.documentElement.style.setProperty('--primaryColor', style.common.colors.primary);
        }
        if(style.common.colors.secondary) {
            document.documentElement.style.setProperty('--secondaryColor', style.common.colors.secondary);
        }
        if(style.common.colors.textOnImage) {
            document.documentElement.style.setProperty('--textOnImageColor', style.common.colors.textOnImage);
        }
        if(style.common.colors.background) {
            document.documentElement.style.setProperty('--bs-body-bg', style.common.colors.background);
            document.documentElement.style.setProperty('--backgroundColor', style.common.colors.background);
        }
        if(style.common.colors.backgroundContrast) {
            document.documentElement.style.setProperty('--backgroundContrastColor', style.common.colors.backgroundContrast);
        }
        if(style.common.colors.modalBackground) {
            document.documentElement.style.setProperty('--modalBackgroundColor', style.common.colors.modalBackground);
        }
        if(style.common.colors.button) {
            document.documentElement.style.setProperty('--bs-btn-bg', style.common.colors.button);
            document.documentElement.style.setProperty('--bs-btn-border-color', style.common.colors.button);
            document.documentElement.style.setProperty('--bs-btn-hover-color', style.common.colors.button);
            document.documentElement.style.setProperty('--bs-btn-hover-border-color', style.common.colors.button);
            document.documentElement.style.setProperty('--bs-btn-active-bg', style.common.colors.button);
            document.documentElement.style.setProperty('--bs-btn-disabled-bg', style.common.colors.button);
            document.documentElement.style.setProperty('--bs-btn-disabled-border-color', style.common.colors.button);
            document.documentElement.style.setProperty('--buttonColor', style.common.colors.button);
        }
        if(style.common.colors.buttonText) {
            document.documentElement.style.setProperty('--buttonTextColor', style.common.colors.buttonText);
        }
        if(style.common.colors.buttonSecondary) {
            document.documentElement.style.setProperty('--buttonSecondaryColor', style.common.colors.buttonSecondary);
        }
        if(style.common.colors.buttonSecondaryText) {
            document.documentElement.style.setProperty('--buttonSecondaryTextColor', style.common.colors.buttonSecondaryText);
        }

        if(style.common.colors.buttonOnImage) {
            document.documentElement.style.setProperty('--buttonOnImageColor', style.common.colors.buttonOnImage);
        }
        if(style.common.colors.buttonOnImageText) {
            document.documentElement.style.setProperty('--buttonOnImageTextColor', style.common.colors.buttonOnImageText);
        }
        
        if(style.common.colors.text) {
            document.documentElement.style.setProperty('--textColor', style.common.colors.text);
        }
        if(style.common.colors.border) {
            document.documentElement.style.setProperty('--borderColor', style.common.colors.border);
        }
        if(style.common.colors.textLight) {
            document.documentElement.style.setProperty('--textLightColor', style.common.colors.textLight);
        }
        if(style.common.colors.textDark) {
            document.documentElement.style.setProperty('--textDarkColor', style.common.colors.textDark);
        }
        if(style.common.colors.textMuted) {
            document.documentElement.style.setProperty('--textMutedColor', style.common.colors.textMuted);
        }

    }

    private setMetaTags(meta: any) {
        // Set the title
        this.titleService.setTitle(meta.title);

        // Set meta description
        this.updateTag('name', 'description', meta.description);

        // Set meta keywords
        this.updateTag('name', 'keywords', meta.keywords);

        // Set Open Graph meta tags
        this.updateTag('property', 'og:locale', meta.ogLocale);
        this.updateTag('property', 'og:title', meta.ogTitle);
        this.updateTag('property', 'og:description', meta.ogDescription);
        this.updateTag('property', 'og:image', meta.ogImage);
        this.updateTag('property', 'og:url', meta.ogUrl);
        this.updateTag('property', 'og:site_name', meta.ogSiteName);
        this.updateTag('property', 'og:type', meta.ogType);

        // Set Twitter meta tags
        this.updateTag('name', 'twitter:card', meta.twitterCard);
        this.updateTag('name', 'twitter:title', meta.twitterTitle);
        this.updateTag('name', 'twitter:description', meta.twitterDescription);
        this.updateTag('name', 'twitter:image', meta.twitterImage);
        this.updateTag('name', 'twitter:url', meta.twitterUrl);
        this.updateTag('name', 'twitter:domain', meta.twitterDomain);

        // Set canonical URL
        const link: HTMLLinkElement = document.querySelector("link[rel='canonical']") || document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', meta.canonicalUrl);
        document.head.appendChild(link);

        // Set favicon
        const link1: HTMLLinkElement = document.querySelector("link[rel='icon']") || document.createElement('link');
        link1.setAttribute('rel', 'icon');
        link1.setAttribute('href', meta.favicon);
        document.head.appendChild(link1);
    }

    private updateTag(attrName: string, attrValue: string, content: string) {
        let tag = document.querySelector(`meta[${attrName}="${attrValue}"]`);
        if (!tag) {
            tag = document.createElement('meta');
            tag.setAttribute(attrName, attrValue);
            document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
    }

    recallJsFuntions() {
        this.routerSubscription = this.router.events
        .pipe(
            filter(event => event instanceof NavigationEnd || event instanceof NavigationCancel),
            takeUntil(this.destroy$)
        )
        .subscribe(event => {
            this.location = this.router.url;
            if (!(event instanceof NavigationEnd)) {
                return;
            }
            window.scrollTo(0, 0);
        });
    }

}